import type { MigrationManifest, DotSign } from '../types';

const BASE = import.meta.env.BASE_URL || '/';

export const DATA_URLS = {
  manifest: `${BASE}data/manifest.json`,
  counties: `${BASE}data/counties.geojson`,
  states: `${BASE}data/states.geojson`,
  dots: (year: number, sign: DotSign) => `${BASE}data/dots/${year}-${sign}.bin`,
} as const;

export class MissingDataError extends Error {
  constructor(url: string) {
    super(
      `Couldn't load ${url}. Run \`npm run data\` to download the Census files and generate public/data.`,
    );
    this.name = 'MissingDataError';
  }
}

export async function loadManifest(signal?: AbortSignal): Promise<MigrationManifest> {
  const res = await fetch(DATA_URLS.manifest, { signal });
  if (!res.ok) throw new MissingDataError(DATA_URLS.manifest);

  // A dev server happily returns index.html for a missing file; catch that.
  const text = await res.text();
  try {
    return JSON.parse(text) as MigrationManifest;
  } catch {
    throw new MissingDataError(DATA_URLS.manifest);
  }
}

/**
 * Dot files are raw little-endian Float32 [lng, lat] pairs -- 8 bytes per dot,
 * about a tenth the size of the equivalent GeoJSON.
 */
export async function loadDots(
  year: number,
  sign: DotSign,
  signal?: AbortSignal,
): Promise<Float32Array> {
  const url = DATA_URLS.dots(year, sign);
  const res = await fetch(url, { signal });
  if (!res.ok) throw new MissingDataError(url);
  const buffer = await res.arrayBuffer();
  return new Float32Array(buffer);
}

/**
 * All the dots for one year collapse into a single MultiPoint feature. Mapbox
 * draws every coordinate as its own circle, and one feature keeps memory and
 * tiling cost far below one feature per dot.
 */
export function dotsToGeoJson(coords: Float32Array): GeoJSON.FeatureCollection {
  const positions: GeoJSON.Position[] = new Array(coords.length / 2);
  for (let i = 0; i < positions.length; i += 1) {
    positions[i] = [coords[i * 2], coords[i * 2 + 1]];
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'MultiPoint', coordinates: positions },
      },
    ],
  };
}

export const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};
